import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../hooks/useCart';
import { useAuth } from '../context/AuthContext';
import { db } from '../config/firebase';
import { collection, doc, addDoc, runTransaction, getDoc } from 'firebase/firestore';
import { Order, OrderItem } from '../types';
import { CartItem } from '../context/cartContext';
import Kinyonga from './Kinyonga';

interface ShoppingCartProps {
  isOpen: boolean;
  onClose: () => void;
}

const ShoppingCart: React.FC<ShoppingCartProps> = ({ isOpen, onClose }) => {
  const { cartItems, removeItem, updateQuantity, getCartTotal, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'wallet' | 'mpesa'>('mpesa');
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [deliveryAddress, setDeliveryAddress] = useState(user?.address || '');
  const [phone, setPhone] = useState(user?.phone || '');

  // Pre-fill saved M-Pesa phone from user profile
  useEffect(() => {
    if (!user?.id) return;
    getDoc(doc(db, 'users', user.id)).then(snap => {
      const saved = snap.data()?.savedMpesaPhone;
      if (saved && !phone) setPhone(saved);
    }).catch(() => {});
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);

  const handleCheckout = async () => {
    if (!user) {
      alert('Please sign in to continue with checkout');
      onClose();
      navigate('/login');
      return;
    }
    
    if (cartItems.length === 0) {
      setCheckoutError('Your cart is empty');
      return;
    }

    if (!isAddressModalOpen) {
      setIsAddressModalOpen(true);
      return;
    }

    if (!deliveryAddress.trim()) {
      setCheckoutError('Please enter a delivery address');
      return;
    }

    if (!phone.trim()) {
      setCheckoutError('Please enter a phone number');
      return;
    }

    let formattedPhone = phone.trim();
    if (formattedPhone.startsWith('0')) {
      formattedPhone = `+254${formattedPhone.substring(1)}`;
    } else if (!formattedPhone.startsWith('+')) {
      formattedPhone = `+${formattedPhone}`;
    }
    if (!formattedPhone.match(/^\+254[0-9]{9}$/)) {
        setCheckoutError('Please enter a valid Kenyan phone number (e.g., +2547... or 07...)');
        return;
    }

    if (paymentMethod === 'wallet' && (user?.walletBalance || 0) < getCartTotal()) {
      setCheckoutError('Insufficient wallet balance. Please add funds or choose M-Pesa');
      return;
    }

    setIsCheckingOut(true);
    setCheckoutError(null);
    setSuccess(null);

    try {
      const orderItems: OrderItem[] = cartItems.map(item => ({
        id: item.product.id,
        name: item.product.name,
        price: item.product.price,
        quantity: item.quantity,
        image: item.product.image || undefined,
      }));

      const now = new Date();
      const cartTotal = getCartTotal();
      const order: Omit<Order, 'id'> = {
        userId: user.id,
        items: orderItems,
        total: cartTotal,
        status: paymentMethod === 'wallet' ? ('payment_successful' as any) : ('pending payment' as any),
        date: now.toISOString(),
        createdAt: now.toISOString(),
        deliveryAddress,
        phone: formattedPhone,
        paymentMethod,
        paymentStatus: paymentMethod === 'wallet' ? ('completed' as any) : ('pending' as any),
      };

      if (paymentMethod === 'wallet') {
        try {
          const ordersRef = collection(db, 'orders');
          const orderDocRef = doc(ordersRef);
          const paymentTxRef = doc(collection(db, 'transactions'), `payment_${orderDocRef.id}_${Date.now()}`);
          const nowIso = now.toISOString();

          await runTransaction(db, async (transaction) => {
            const userRef = doc(db, 'users', user.id);
            const userDoc = await transaction.get(userRef);
            const currentBalance = Number(userDoc.data()?.walletBalance || 0);

            if (currentBalance < cartTotal) {
              throw new Error('Insufficient wallet balance.');
            }

            transaction.update(userRef, {
              walletBalance: currentBalance - cartTotal,
              updatedAt: nowIso,
            });

            transaction.set(orderDocRef, {
              ...order,
              createdAt: nowIso,
              updatedAt: nowIso,
              items: orderItems,
            });

            transaction.set(paymentTxRef, {
              userId: user.id,
              type: 'order_payment',
              amount: cartTotal,
              status: 'completed',
              createdAt: nowIso,
              updatedAt: nowIso,
              orderId: orderDocRef.id,
              description: 'Wallet payment',
            });
          });

          setSuccess('Payment successful! Redirecting to order confirmation...');
          clearCart();
          setIsAddressModalOpen(false);
          navigate(`/order-confirmation/${orderDocRef.id}`);
        } catch (walletError: any) {
          console.error('Wallet payment error:', walletError);
          setCheckoutError(walletError?.message || 'Wallet payment failed.');
        }
      }

      if (paymentMethod === 'mpesa') {
        try {
          // Create Firestore order first (mpesaDeposit requires `status: "pending payment"`).
          const createdOrderRef = await addDoc(collection(db, 'orders'), {
            ...order,
            status: 'pending payment',
            paymentMethod: 'mpesa',
            paymentStatus: 'pending',
            createdAt: now.toISOString(),
            updatedAt: now.toISOString(),
          });

          const createdOrderId = createdOrderRef.id;

          const mpesaResponse = await fetch('/.netlify/functions/mpesaDeposit', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              phone: formattedPhone,
              amount: cartTotal,
              orderId: createdOrderId,
              userId: user.id,
            }),
          });

          const mpesaData = await mpesaResponse.json();

          if (!mpesaResponse.ok || !mpesaData.success) {
            throw new Error(mpesaData.message || 'M-Pesa payment initiation failed.');
          }

          setSuccess(`M-Pesa prompt sent to ${formattedPhone}. Please complete the payment.`);
          clearCart();
          setIsAddressModalOpen(false);
          navigate(`/order-confirmation/${createdOrderId}`);
        } catch (mpesaError: any) {
          console.error('M-Pesa initiation error:', mpesaError);
          setCheckoutError(
            'Failed to initiate M-Pesa payment. Please try again.' +
              (mpesaError instanceof Error ? ' ' + mpesaError.message : '')
          );
        }
      }
      
    } catch (error) {
      setCheckoutError((error as Error).message || 'An error occurred during checkout');
    } finally {
      setIsCheckingOut(false);
    }
  };

  React.useEffect(() => {
      if (!isAddressModalOpen) {
          setCheckoutError(null);
          setSuccess(null);
      }
  }, [isAddressModalOpen]);

  return (
    // <AnimatePresence>
      <>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              if (!isCheckingOut) {
                setIsAddressModalOpen(false);
                onClose();
              }
            }}
          />
          
          <motion.div
            className="fixed right-0 top-0 h-full w-full sm:w-96 bg-background border-l border-border shadow-xl z-50 overflow-y-auto"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
          >
            <div className="p-4 flex flex-col h-full">
              <div className="flex justify-between items-center border-b border-border pb-4">
                <h2 className="text-xl font-semibold">Your Cart</h2>
                <button
                  onClick={() => {
                    if (!isCheckingOut) {
                      setIsAddressModalOpen(false);
                      onClose();
                    }
                  }}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  aria-label="Close cart"
                  disabled={isCheckingOut}
                >
                  ✕
                </button>
              </div>
              
              {!isAddressModalOpen ? (
                <>
                  {cartItems.length === 0 ? (
                    <div className="flex flex-col items-center justify-center flex-grow py-12">
                      <Kinyonga mood="sleepy" size={160}
                        caption="Your cart is empty — let's grow something." className="mb-5" />
                      <Link
                        to="/products"
                        className="btn btn-primary"
                        onClick={onClose}
                      >
                        Browse Products
                      </Link>
                      <a href="/play" onClick={onClose}
                        className="mt-3 text-xs text-primary hover:underline">
                        🎮 Or play Shamba — earn up to KES 200 off your first order
                      </a>
                    </div>
                  ) : (
                    <> 
                      <div className="flex-grow overflow-y-auto py-4">
                        {/* <AnimatePresence> */}
                          {cartItems.map((item: CartItem) => (
                            <motion.div
                              key={item.product.id}
                              className="flex items-center py-4 border-b border-border"
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              <div className="w-16 h-16 flex-shrink-0 bg-card rounded overflow-hidden mr-4">
                                {item.product.image ? (
                                  <img
                                    src={item.product.image}
                                    alt={item.product.name}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center bg-muted">
                                    <i className="fas fa-seedling text-primary text-xl"></i>
                                  </div>
                                )}
                              </div>
                              
                              <div className="flex-grow">
                                <h3 className="font-medium">{item.product.name}</h3>
                                <p className="text-primary font-mono text-sm">KES {item.product.price.toFixed(2)}</p>
                              </div>
                              
                              <div className="flex items-center">
                                <div className="flex items-center rounded-full border-2 border-border bg-card overflow-hidden">
                                  <button
                                    className="w-9 h-9 flex items-center justify-center text-lg font-bold text-primary hover:bg-muted active:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                    onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                                    aria-label="Decrease quantity"
                                    disabled={isCheckingOut}
                                  >
                                    −
                                  </button>
                                  <input
                                    type="number"
                                    min={1}
                                    inputMode="numeric"
                                    value={item.quantity}
                                    onChange={e => {
                                      const v = parseInt(e.target.value, 10);
                                      if (!isNaN(v) && v >= 1) updateQuantity(item.product.id, v);
                                    }}
                                    disabled={isCheckingOut}
                                    aria-label={`Quantity of ${item.product.name}`}
                                    className="w-12 text-center font-mono font-semibold text-sm bg-transparent outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                  />
                                  <button
                                    className="w-9 h-9 flex items-center justify-center text-lg font-bold text-primary hover:bg-muted active:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                    onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                                    aria-label="Increase quantity"
                                    disabled={isCheckingOut}
                                  >
                                    +
                                  </button>
                                </div>
                                <button
                                  className="ml-3 w-7 h-7 flex items-center justify-center rounded-full text-red-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                  onClick={() => removeItem(item.product.id)}
                                  aria-label="Remove item"
                                  disabled={isCheckingOut}
                                >
                                  <i className="fas fa-trash-alt text-xs"></i>
                                </button>
                              </div>
                            </motion.div>
                          ))}
                        {/* </AnimatePresence> */}
                      </div>
                      
                      <div className="border-t border-border pt-4 mt-auto">
                        <div className="flex justify-between mb-4">
                          <span className="font-medium">Subtotal:</span>
                          <span className="font-semibold font-mono">KES {getCartTotal().toFixed(2)}</span>
                        </div>
                        <div className="mb-4 text-sm text-muted-foreground">
                          <p>Delivery fee will be calculated at checkout.</p>
                        </div>
                        <button
                          className="btn btn-primary w-full mb-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={handleCheckout}
                           disabled={cartItems.length === 0 || isCheckingOut || !user}
                        >
                           {cartItems.length === 0 ? 'Cart is Empty' : isCheckingOut ? 'Loading Address Form...' : 'Proceed to Checkout'}
                        </button>
                        <button
                          className="btn btn-secondary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={clearCart}
                           disabled={cartItems.length === 0 || isCheckingOut}
                        >
                          Clear Cart
                        </button>
                         {!user && <p className="text-yellow-500 text-sm text-center mt-2">Please log in to checkout.</p>}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="py-4 flex-grow flex flex-col">
                  <h3 className="text-lg font-semibold mb-4">Delivery Information</h3>
                  
                  {checkoutError && (
                    <div className="bg-red-900 bg-opacity-30 border border-red-700 text-red-200 p-3 rounded mb-4">
                      {checkoutError}
                    </div>
                  )}
                   {success && (
                    <div className="bg-green-900 bg-opacity-30 border border-green-700 text-green-200 p-3 rounded mb-4">
                      {success}
                    </div>
                  )}
                  
                  <div className="space-y-4 mb-6 flex-grow">
                    <div>
                      <label className="block text-sm font-medium mb-1">Delivery Address</label>
                      <textarea
                        value={deliveryAddress}
                        onChange={(e) => setDeliveryAddress(e.target.value)}
                        className="w-full p-3 bg-background border border-gray-700 rounded text-black dark:text-white"
                        rows={3}
                        placeholder="Enter your full delivery address"
                        required
                         disabled={isCheckingOut}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-1">Phone Number</label>
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="w-full p-3 bg-background border border-gray-700 rounded text-black dark:text-white"
                        placeholder="+254..."
                        required
                         disabled={isCheckingOut}
                      />
                    </div>
                  </div>
                  
                  <h3 className="text-lg font-semibold mb-4">Payment Method</h3>
                  
                  <div className="space-y-3 mb-6">
                    <div 
                      className={`border ${paymentMethod === 'mpesa' ? 'border-primary' : 'border-gray-700'} rounded-md p-3 cursor-pointer`}
                      onClick={() => setPaymentMethod('mpesa')}
                    >
                      <div className="flex items-center">
                        <div className={`w-4 h-4 rounded-full border ${paymentMethod === 'mpesa' ? 'border-primary' : 'border-gray-500'} mr-2 flex items-center justify-center`}>
                          {paymentMethod === 'mpesa' && <div className="w-2 h-2 rounded-full bg-primary"></div>}
                        </div>
                        <div className="ml-2">
                          <span className="font-medium">M-Pesa</span>
                          <p className="text-xs text-muted-foreground">Pay using your M-Pesa mobile money</p>
                        </div>
                      </div>
                    </div>
                    
                    <div 
                      className={`border ${paymentMethod === 'wallet' ? 'border-primary' : 'border-gray-700'} rounded-md p-3 cursor-pointer`}
                      onClick={() => setPaymentMethod('wallet')}
                    >
                      <div className="flex items-center">
                        <div className={`w-4 h-4 rounded-full border ${paymentMethod === 'wallet' ? 'border-primary' : 'border-gray-500'} mr-2 flex items-center justify-center`}>
                          {paymentMethod === 'wallet' && <div className="w-2 h-2 rounded-full bg-primary"></div>}
                        </div>
                        <div className="ml-2">
                          <span className="font-medium">Wallet</span>
                          <p className="text-xs text-muted-foreground">Pay using your wallet balance: KES {user?.walletBalance?.toFixed(2) || '0.00'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="pt-4 border-t border-border mt-auto">
                    <div className="flex justify-between mb-4">
                      <span className="font-medium">Total:</span>
                      <span className="font-semibold">KES {getCartTotal().toFixed(2)}</span>
                    </div>
                    
                    <button
                      className="btn btn-primary w-full mb-2"
                      onClick={handleCheckout}
                      disabled={isCheckingOut}
                    >
                      {isCheckingOut ? 'Processing...' : 'Complete Order'}
                    </button>
                    
                    <button
                      className="btn btn-secondary w-full"
                      onClick={() => setIsAddressModalOpen(false)}
                      disabled={isCheckingOut}
                    >
                      Back to Cart
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
      </>
    // </AnimatePresence>
  );
};

export default ShoppingCart;
